import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Radio,
  RadioGroup,
  Stack,
  Divider,
  Text,
  Flex,
  useToast,
  Input,
  Select,
  Box,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { baseToastProps, toastTypes } from '../constants';
import {
  createBillingMethod,
  getStateByZip,
  makePayment,
} from '../utils/apiCalls';
import { RiSecurePaymentFill } from 'react-icons/ri';
import { getMakePaymentVaultCompany } from '../utils/helperServices';

const DelinquentMakePaymentModal = ({
  isOpen,
  onClose,
  billingMethods,
  outstandingBillings, // still using these to evaluate new card business name(location specific or for partner)
  delinquentPayments,
  setReload,
}) => {
  const [selectedMethod, setSelectedMethod] = useState('-1');
  const [cardObj, setCardObj] = useState({});
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [zip, setZip] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('US');

  useEffect(() => {
    const handleCollectJSResponse = response => {
      if (!response.token) {
        toast(toastTypes.cardTokenCreationError);
        return;
      }
      const { token, card } = response;
      const { number, exp } = card;
      if (!token || !card || !number || !exp) {
        toast(toastTypes.cardTokenCreationError);
        return;
      }
      const lastFour = number.substr(number.length - 4);
      const expirationDate = exp.substring(0, 2) + '/' + exp.substring(2);
      setCardObj({ expirationDate, lastFour, token, number });
    };

    window.CollectJS.configure({
      instructionText: 'Enter Card Information',
      variant: 'lightbox',
      theme: 'material',
      buttonText: 'Submit',
      primaryColor: '#008080',
      currency: 'USD',
      country: 'US',
      callback: response => handleCollectJSResponse(response),
    });
  }, [toast]);



  const initPayment = e => {
    e.preventDefault();
    window.CollectJS.startPaymentRequest();
  };

  const billingMethodOnChange = value => {
    setSelectedMethod(value);
    setCardObj({});
  };

  const zipOnBlur = async e => {
    if (country === 'CA') {
      return;
    }
    const zip = e.target.value;
    if (zip.length < 5) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const { error, msg, city, state } = await getStateByZip(zip);
    if (error || !city || !state) {
      toast({ ...baseToastProps, status: 'error', description: msg });
      setCity('');
      setState('');
      setIsLoading(false);
      return;
    }
    setCity(city);
    setState(state);
    setIsLoading(false);
  };

  const zipOnChange = e => {
    let newZip = e.target.value;
    if (country === 'CA') {
      setZip(newZip);
      return;
    }
    if (newZip.length > 5) {
      return;
    }
    setZip(newZip);
  };

  const makePaymentHandler = async () => {
    let billingMethodId =
      billingMethods[selectedMethod] && billingMethods[selectedMethod].id;
    if (selectedMethod === '-1') {
      toast(toastTypes.noPaymentMethodSelected);
      return;
    }

    if (selectedMethod === 'newMethod') {
      if (!cardObj || !cardObj.token) {
        toast(toastTypes.missingCardDetails);
        return;
      }
      if (!validateAddress()) {
        return;
      }
    }

    setIsLoading(true);

    if (cardObj && cardObj.token) {
      const address = {
        firstName,
        lastName,
        city,
        state,
        streetAddress,
        zipCode: zip,
      };
      const company = getMakePaymentVaultCompany(outstandingBillings);
      const reqData = { address, ...cardObj, company };
      const { success, id, sessionExpired } = await createBillingMethod(reqData);
      if (!success || !id) {
        toast(sessionExpired ? toastTypes.sessionExpired : toastTypes.cardTokenCreationError);
        setIsLoading(false);
        return;
      }
      billingMethodId = id;
    }

    const reqData = {
      billingMethodId,
      invoiceIds: delinquentPayments.subscriptions.flatMap(
        sub => sub.invoiceIds
      ),
      proRatedInvoice: delinquentPayments.proRatedInvoice,
    };

    const { success, allTransactionsSuccessful, sessionExpired } = await makePayment(reqData);

    if (success && allTransactionsSuccessful) {
      toast(toastTypes.paymentSuccess);
    }

    if (success && !allTransactionsSuccessful) {
      toast(toastTypes.someTransactionsFailed);
    }

    if (!success) {
      toast(sessionExpired ? toastTypes.sessionExpired : toastTypes.allTransactionsFailed);
    }

    setReload(true);
    onClose();
  };

  const validateAddress = () => {
    let missingField;
    if (firstName.trim() === '') {
      missingField = 'First Name';
    } else if (lastName.trim() === '') {
      missingField = 'Last Name';
    } else if (streetAddress.trim() === '') {
      missingField = 'Street Address';
    } else if (zip.trim() === '') {
      missingField = 'Zip Code';
    } else if (state.trim() === '') {
      missingField = 'State';
    } else if (city.trim() === '') {
      missingField = 'City';
    }
    if (missingField) {
      toast(toastTypes.missingRequiredField(missingField));
      return false;
    }
    return true;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      closeOnOverlayClick={false}
      trapFocus={false}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Billing Methods</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <RadioGroup
            onChange={billingMethodOnChange}
            value={selectedMethod}
            isDisabled={isLoading}
          >
            <Stack direction="column">
              {billingMethods.map((billingMethod, index) => (
                <Radio color={'gray.100'} value={`${index}`} key={index}>
                  Ending ...{billingMethod.lastFour}
                </Radio>
              ))}
              <Radio color={'gray.100'} value={'newMethod'}>
                Add New Billing Method
              </Radio>
            </Stack>
          </RadioGroup>

          {selectedMethod === 'newMethod' && (
            <Flex direction="column" mr={3} ml={3} mt={2}>
              <Select
                value={country}
                placeholder="Select country"
                onChange={e => setCountry(e.target.value)}
              >
                <option value="US">US</option>
                <option value="CA">CA</option>
              </Select>
              <Flex w="100%" mt={3} mb={3} justify="space-between">
                <Input
                  w="48%"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  placeholder="First Name"
                />
                <Input
                  w="48%"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  placeholder="Last Name"
                />
              </Flex>
              <Input
                value={streetAddress}
                onChange={e => setStreetAddress(e.target.value)}
                mt={0.1}
                mb={3}
                placeholder="Street Address"
              />
              <Flex w="100%" mt={0.1} mb={3} justify="space-between">
                <Input
                  w="48%"
                  value={zip}
                  type={country === 'US' ? 'number' : 'text'}
                  onBlur={e => zipOnBlur(e)}
                  placeholder="Zip Code"
                  onChange={e => zipOnChange(e)}
                />
                <Input
                  w="48%"
                  value={state}
                  onChange={e => setState(e.target.value)}
                  placeholder="State Code (ex: FL, GA)"
                />
              </Flex>
              <Input
                value={city}
                onChange={e => setCity(e.target.value)}
                mt={0.1}
                mb={3}
                placeholder="City"
              />
              <Flex width="100%" justifyContent={'space-evenly'}></Flex>
              <Flex
                direction={'row'}
                w="100%"
                alignContent={'center'}
                justifyContent="center"
                mt={0.1}
                alignSelf="center"
              >
                {cardObj && cardObj.token && (
                  <CheckIcon color="green" mr={2} alignSelf="center" />
                )}
                <Button
                  isLoading={isLoading}
                  variant="link"
                  onClick={e => initPayment(e)}
                  _hover={{ textDecoration: 'underline', color: '#FF9200' }}
                  leftIcon={<RiSecurePaymentFill />}
                >
                  {cardObj && cardObj.token
                    ? `New card ${cardObj.number}`
                    : `Enter Card`}
                </Button>
              </Flex>
              {cardObj && cardObj.token && (
                <Button
                  isLoading={isLoading}
                  ml={4}
                  mt={2}
                  onClick={() => setCardObj({})}
                  color="tomato"
                  variant={'link'}
                  _hover={{ textDecoration: 'underline', color: '#FF9200' }}
                >
                  Remove Card
                </Button>
              )}
            </Flex>
          )}

          <Divider my={3} />
          <Box maxHeight="300px" overflowY="auto" pr={2}>
            {delinquentPayments.subscriptions.map((subscription, index) => (
              <Flex
                key={index}
                justify="space-between"
                align="center"
                py={2}
                px={2}
                borderRadius="md"
                _hover={{ bg: 'gray.50' }}
              >
                <Text fontSize="sm" noOfLines={2}>
                  {subscription.subscriptionName}
                </Text>
                <Text fontWeight="medium" fontSize="sm">
                  ${subscription.totalAmount}
                </Text>
              </Flex>
            ))}
          </Box>
          <Flex justifyContent="space-between" fontWeight={'bold'}>
            <Text>Total:</Text>
            <Text>${delinquentPayments.totalToPay}</Text>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'}>
          <Button variant="ghost" onClick={onClose} isLoading={isLoading}>
            Close
          </Button>
          <Button onClick={() => makePaymentHandler()} isLoading={isLoading}>
            Make Payment
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DelinquentMakePaymentModal;
